import { useStore } from "@nanostores/react";
import { uiCapabilityKeys } from "@vega-api/extended/auth/uiCapabilities";
import {
  $uiCapabilityOverrides,
  clearAllCapabilityOverrides,
  clearCapabilityOverride,
  setAllCapabilityOverrides,
  setCapabilityOverride,
} from "@vega-api/extended/auth/uiCapabilityOverrides";
import {
  FeatureFlag,
  useFeatureFlag,
} from "@vega-api/extended/auth/useFeatureFlag";
import { useUiCapabilities } from "@vega-api/extended/auth/useUiCapabilities";
import { cnTwMerge } from "@vega-core/utils/cnTwMerge";

import { OverridePanel } from "./OverridePanel";

type Props = {
  isOpen: boolean;
  onClose: () => void;
};

export function UiCapabilityPanel({ isOpen, onClose }: Props) {
  // Avoid the @nanostores subscription and the capability queries when the
  // panel is closed — the parent palette mounts this unconditionally.
  if (!isOpen) return null;

  return <UiCapabilityPanelContent onClose={onClose} />;
}

function UiCapabilityPanelContent({ onClose }: { onClose: () => void }) {
  const overrides = useStore($uiCapabilityOverrides);
  const { capabilities } = useUiCapabilities();
  const { featureFlag } = useFeatureFlag();
  const isApiEnabled = Boolean(
    featureFlag[FeatureFlag.GP_AUTH_UI_CAPABILITIES],
  );

  return (
    <OverridePanel
      dataTestIdPrefix="ui-capability"
      effectiveValues={capabilities}
      headerContent={
        <div
          className="flex items-center gap-2 body-small-regular text-text/secondary"
          data-testid="ui-capability-flag-status"
        >
          <span
            className={cnTwMerge(
              "px-2 py-0.5 rounded body-small-medium",
              isApiEnabled
                ? "bg-brand-default text-text/on-brand"
                : "bg-background/secondary text-text/secondary",
            )}
          >
            {FeatureFlag.GP_AUTH_UI_CAPABILITIES}: {isApiEnabled ? "ON" : "OFF"}
          </span>
          <span>
            {isApiEnabled
              ? "values from capabilities API"
              : "values derived from legacy permissions"}
          </span>
        </div>
      }
      keys={uiCapabilityKeys}
      onClearAll={clearAllCapabilityOverrides}
      onClearOverride={clearCapabilityOverride}
      onClose={onClose}
      onSetAll={(value) => setAllCapabilityOverrides(uiCapabilityKeys, value)}
      onSetOverride={setCapabilityOverride}
      overrides={overrides}
      title="UI Capabilities"
    />
  );
}
